import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Shield, UserPlus, UserMinus, Trash2, Clock, Ban, 
  MoreVertical, Check, X, Settings 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StreamModerationPanel({ 
  streamId, 
  isCreator,
  onClose 
}) {
  const { token } = useAuth();
  const [moderators, setModerators] = useState([]);
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('moderators');
  const [showAddMod, setShowAddMod] = useState(false);
  const [newModUsername, setNewModUsername] = useState('');
  const [newModPermissions, setNewModPermissions] = useState(['delete_message', 'timeout_user']);

  useEffect(() => {
    fetchModerators();
    fetchBans();
  }, [streamId]);

  const fetchModerators = async () => {
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/moderators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setModerators(data.moderators || []);
      }
    } catch (err) {
      console.error('Failed to fetch moderators:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/bans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBans(data.bans || []);
      }
    } catch (err) {
      console.error('Failed to fetch bans:', err);
    }
  };

  const handleAddModerator = async () => {
    if (!newModUsername.trim()) {
      toast.error('Entre un nom d\'utilisateur');
      return;
    }

    try {
      // First, find user by username
      const searchRes = await fetch(`${API_URL}/api/users/${newModUsername.toLowerCase()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!searchRes.ok) {
        toast.error('Utilisateur non trouvé');
        return;
      }

      const userData = await searchRes.json();

      const res = await fetch(`${API_URL}/api/streams/${streamId}/moderators`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: userData.id,
          permissions: newModPermissions
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Modérateur ajouté!');
        setShowAddMod(false);
        setNewModUsername('');
        fetchModerators();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleRemoveModerator = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/moderators/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Modérateur retiré');
        fetchModerators();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const handleUnban = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/ban/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Utilisateur débanni');
        fetchBans();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  const togglePermission = (perm) => {
    setNewModPermissions(prev => 
      prev.includes(perm) 
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg" data-testid="stream-moderation-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-orange-500" />
            Gestion de la Modération
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Gère les modérateurs et les utilisateurs bannis de ton stream.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-800 pb-2">
          <Button
            variant={activeTab === 'moderators' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('moderators')}
            className={activeTab === 'moderators' ? 'bg-orange-500' : ''}
          >
            <Shield className="h-4 w-4 mr-1" />
            Modérateurs ({moderators.length})
          </Button>
          <Button
            variant={activeTab === 'bans' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('bans')}
            className={activeTab === 'bans' ? 'bg-red-500' : ''}
          >
            <Ban className="h-4 w-4 mr-1" />
            Bannis ({bans.length})
          </Button>
        </div>

        <ScrollArea className="h-[300px]">
          {/* Moderators Tab */}
          {activeTab === 'moderators' && (
            <div className="space-y-3">
              {isCreator && (
                <Button
                  onClick={() => setShowAddMod(true)}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="add-moderator-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Ajouter un modérateur
                </Button>
              )}

              {moderators.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Aucun modérateur pour ce stream
                </p>
              ) : (
                moderators.map((mod) => (
                  <div 
                    key={mod.id} 
                    className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                    data-testid={`moderator-${mod.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={mod.user?.profile_picture} />
                        <AvatarFallback className="bg-orange-500">
                          {mod.user?.display_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{mod.user?.display_name}</p>
                        <p className="text-xs text-gray-400">@{mod.user?.username}</p>
                        <div className="flex gap-1 mt-1">
                          {mod.permissions?.map((perm) => (
                            <Badge key={perm} variant="outline" className="text-xs border-gray-600">
                              {perm === 'delete_message' && '🗑️'}
                              {perm === 'timeout_user' && '⏱️'}
                              {perm === 'ban_user' && '🚫'}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    {isCreator && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveModerator(mod.user_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Bans Tab */}
          {activeTab === 'bans' && (
            <div className="space-y-3">
              {bans.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Aucun utilisateur banni
                </p>
              ) : (
                bans.map((ban) => (
                  <div 
                    key={ban.id} 
                    className="flex items-center justify-between p-3 bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={ban.user?.profile_picture} />
                        <AvatarFallback className="bg-red-500">
                          {ban.user?.display_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{ban.user?.display_name}</p>
                        <p className="text-xs text-gray-400">@{ban.user?.username}</p>
                        {ban.reason && (
                          <p className="text-xs text-red-400 mt-1">
                            Raison: {ban.reason}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUnban(ban.user_id)}
                      className="border-green-500 text-green-500 hover:bg-green-500/10"
                    >
                      Débannir
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </ScrollArea>

        {/* Add Moderator Dialog */}
        {showAddMod && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center rounded-lg">
            <div className="bg-gray-800 p-6 rounded-lg w-full max-w-sm space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-orange-500" />
                Ajouter un modérateur
              </h3>
              
              <Input
                placeholder="Nom d'utilisateur"
                value={newModUsername}
                onChange={(e) => setNewModUsername(e.target.value)}
                className="bg-gray-900 border-gray-700"
                data-testid="new-mod-username"
              />

              <div className="space-y-2">
                <p className="text-sm text-gray-400">Permissions:</p>
                <div className="space-y-2">
                  {[
                    { id: 'delete_message', label: 'Supprimer messages', icon: '🗑️' },
                    { id: 'timeout_user', label: 'Timeout utilisateurs', icon: '⏱️' },
                    { id: 'ban_user', label: 'Bannir utilisateurs', icon: '🚫' },
                  ].map((perm) => (
                    <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={newModPermissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                      />
                      <span>{perm.icon} {perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddMod(false)}
                  className="flex-1 border-gray-700"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleAddModerator}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  data-testid="confirm-add-mod-btn"
                >
                  Ajouter
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Quick action menu for chat messages
export function ChatMessageActions({ 
  streamId, 
  messageId, 
  userId,
  username,
  canModerate,
  isCreator,
  onAction 
}) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/chat/${messageId}/moderate`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Message supprimé');
        onAction?.('delete');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeout = async (duration = 300) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/timeout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: userId,
          duration_seconds: duration
        })
      });

      if (res.ok) {
        toast.success(`${username} en timeout pour ${duration / 60} minutes`);
        onAction?.('timeout');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async () => {
    if (!window.confirm(`Bannir ${username} de ce stream?`)) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/streams/${streamId}/ban`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: userId,
          is_permanent: true
        })
      });

      if (res.ok) {
        toast.success(`${username} banni du stream`);
        onAction?.('ban');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (!canModerate) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100"
          disabled={loading}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-gray-800 border-gray-700">
        <DropdownMenuItem onClick={handleDelete} className="text-white hover:bg-gray-700 cursor-pointer">
          <Trash2 className="h-4 w-4 mr-2 text-red-400" />
          Supprimer message
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-gray-700" />
        <DropdownMenuItem onClick={() => handleTimeout(300)} className="text-white hover:bg-gray-700 cursor-pointer">
          <Clock className="h-4 w-4 mr-2 text-yellow-400" />
          Timeout 5 min
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleTimeout(600)} className="text-white hover:bg-gray-700 cursor-pointer">
          <Clock className="h-4 w-4 mr-2 text-yellow-400" />
          Timeout 10 min
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleTimeout(3600)} className="text-white hover:bg-gray-700 cursor-pointer">
          <Clock className="h-4 w-4 mr-2 text-orange-400" />
          Timeout 1 heure
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-gray-700" />
        <DropdownMenuItem onClick={handleBan} className="text-red-400 hover:bg-red-500/10 cursor-pointer">
          <Ban className="h-4 w-4 mr-2" />
          Bannir du stream
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
